// AM dashboard, "High CPL" (Meta and Google, last 30 days). PURE.
//
// An account in the book is listed when its CPL is at least HIGH_CPL_FACTOR x its
// BRAND average CPL (all brand accounts, same channel, same window, same currency),
// and "spending, no leads" accounts sit at the TOP. The rule for the latter is the
// ONE in advertisingHome/noLeads.ts; nothing here reimplements it.

import type { AdChannel, HomeAccount, SpineDay } from '../../types/advertisingHome.js';
import {
  BrandAverages,
  classifyCpl,
  compareVerdictsWorstFirst,
  type CplVerdict,
} from '../advertisingHome/noLeads.js';
import { sumWindow } from '../advertisingHome/spine.js';

export const HIGH_CPL_FACTOR = 1.3;

export interface HighCplRow {
  accountId: string;
  name: string;
  currency: string;
  spend: number;
  leads: number;
  verdict: CplVerdict;
  /** The brand's average CPL for this channel + window + currency (null = none). */
  brandCpl: number | null;
  /** (cpl ÷ brandCpl) - 1 as a ratio; null for no-leads rows (shown as "No leads"). */
  over: number | null;
}

export function buildHighCpl(input: {
  accounts: readonly HomeAccount[];
  rows: readonly SpineDay[];
  channel: AdChannel;
  from: string;
  to: string;
  brandAverages: BrandAverages;
}): HighCplRow[] {
  const byAccount = new Map<string, SpineDay[]>();
  for (const r of input.rows) {
    if (r.channel !== input.channel) continue;
    const list = byAccount.get(r.accountId);
    if (list) list.push(r);
    else byAccount.set(r.accountId, [r]);
  }
  const out: HighCplRow[] = [];
  for (const a of input.accounts) {
    if (!(input.channel === 'meta' ? a.runsMeta : a.runsGoogle)) continue;
    const w = sumWindow(byAccount.get(a.id) ?? [], input.from, input.to);
    const currency = w.currency;
    if (currency === null) continue;
    const spend = w.spendByCurrency.find((x) => x.currency === currency)?.amount ?? 0;
    const leads = w.leadsByCurrency.find((x) => x.currency === currency)?.amount ?? 0;
    const brandCpl = input.brandAverages.cpl(a.brandAccountId, input.channel, currency);
    const verdict = classifyCpl({ spend, leads, brandCpl });
    if (verdict.kind === 'no_leads') {
      out.push({ accountId: a.id, name: a.name, currency, spend, leads, verdict, brandCpl, over: null });
    } else if (verdict.kind === 'cpl' && brandCpl !== null && verdict.cpl >= HIGH_CPL_FACTOR * brandCpl) {
      out.push({
        accountId: a.id,
        name: a.name,
        currency,
        spend,
        leads,
        verdict,
        brandCpl,
        over: verdict.cpl / brandCpl - 1,
      });
    }
  }
  return out.sort(
    (x, y) =>
      compareVerdictsWorstFirst(x.verdict, y.verdict) ||
      (y.over ?? 0) - (x.over ?? 0) ||
      x.name.localeCompare(y.name),
  );
}
